import { useCallback, useEffect, useState } from 'react';
import { InvoiceModel } from '@frontend/api-invoices';
import { isPaidInvoice } from '@frontend/payments-invoice-calculations';
import {
  useGetStripePaymentMethodId,
  useDetachPaymentMethod,
  useAttachPaymentMethod,
} from '@frontend/payments-payment-method';

export type HandleTerminalPaymentMethodProps = {
  locationId: string | undefined;
  invoice: InvoiceModel | undefined;
  paymentUuid: string | undefined;
};

const MAX_RETRIES = 3;
const RETRY_TIMEOUT = 1000;

export const useHandleTerminalPaymentMethod = ({
  locationId,
  paymentUuid,
  invoice,
}: HandleTerminalPaymentMethodProps) => {
  const [isLoadingGetStripePaymentMethod, setIsLoadingGetStripePaymentMethod] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const {
    data,
    isLoading: isLoadingPaymentMethodQuery,
    isFetched: isFetchedPaymentMethod,
    isRefetching: isRefetchingPaymentMethod,
    refetch: refetchPaymentMethod,
  } = useGetStripePaymentMethodId(
    {
      locationId,
      paymentUuid,
    },
    {
      enabled: isPaidInvoice(invoice) && !!paymentUuid,
    }
  );

  const paymentMethodId = data?.paymentMethodId;

  const { isLoading: isDeletingPaymentMethod, mutateAsync: deletePaymentMethodAsync } = useDetachPaymentMethod();

  const deletePaymentMethod = useCallback(
    async ({ locationId }: { locationId: string | undefined }) => {
      if (!paymentMethodId) {
        throw new Error('No payment method ID found for the invoice');
      }
      return await deletePaymentMethodAsync({ paymentMethodId, locationId });
    },
    [paymentMethodId, deletePaymentMethodAsync]
  );

  const { isLoading: isAttachingPaymentMethod, mutateAsync: attachPaymentMethodAsync } = useAttachPaymentMethod();

  /**
   * Reset retries if invoice or location change
   */
  useEffect(() => {
    if (retryCount > 0) {
      setRetryCount(0);
    }
  }, [locationId, invoice?.id]);

  /**
   * The new schema methods will not have a temporal race condition.
   * But for now we need to retry.
   */
  useEffect(() => {
    if (isRefetchingPaymentMethod) {
      return;
    }

    if (isLoadingPaymentMethodQuery) {
      setIsLoadingGetStripePaymentMethod(true);
    } else {
      if (isFetchedPaymentMethod && !paymentMethodId) {
        if (retryCount < MAX_RETRIES) {
          setRetryCount((prev) => prev + 1);
          setTimeout(() => {
            refetchPaymentMethod();
          }, RETRY_TIMEOUT);
        } else {
          console.error('Max retries reached for fetching payment method ID');
          setIsLoadingGetStripePaymentMethod(false);
        }
      } else {
        setIsLoadingGetStripePaymentMethod(false);
      }
    }
  }, [isLoadingPaymentMethodQuery, isFetchedPaymentMethod, isRefetchingPaymentMethod]);

  return {
    isLoadingGetStripePaymentMethod,
    deletePaymentMethod,
    isDeletingPaymentMethod,
    attachPaymentMethod: attachPaymentMethodAsync,
    isAttachingPaymentMethod,
  };
};
